import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import { distance } from '@turf/turf';
import { pointerMove } from 'ol/events/condition';
import { Select } from 'ol/interaction';
import { toLonLat } from 'ol/proj';
import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import actions from '@/actions';
import CommandConverter from '@/helpers/CommandConverter';
import FenceConverter from '@/helpers/FenceConverter';
import OlMap from '@/helpers/OlMap';
import useMissionCommand from '@/hooks/command/mavlink/useMissionCommand';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

export const ArduPilot = Symbol();
export const PX4 = Symbol();

const useMission = (firmware, robot) => {
  const dispatch = useDispatch();
  const address = useSelector((state) => state.telemetry[robot.id]?.address);
  const homePosition = useSelector((state) => state.telemetry[robot.id].homePosition);
  const commands = useSelector((state) => state.missions[robot.id]?.commands);
  const fences = useSelector((state) => state.missions[robot.id]?.fences);
  const map = OlMap.getMap();
  const missionCommand = useMissionCommand();

  const sysId = useMemo(() => {
    return address?.sysId;
  }, [address]);

  useEffect(() => {
    // 제어권한 미보유 시
    if (!robot.isOwned) return;
    if (!sysId) return;

    loadMission();
  }, [sysId]);

  useUpdateEffect(() => {
    OlMap.removeWays(robot.id);
    drawCommand();
  }, [homePosition]);

  useUpdateEffect(() => {
    drawCommand();
    drawFence();

    return () => {
      OlMap.removeWays(robot.id);
      OlMap.removeFences(robot.id);
    };
  }, [commands, fences, robot.color]);

  const loadMission = async () => {
    const commands = await downloadCommands();
    const fences = await downloadFences();

    dispatch(actions.missions.load(robot.id, { commands, fences }));
  };

  const downloadCommands = async () => {
    const messages = [];

    try {
      const { count } = await executeMavLinkCommand(robot, missionCommand.requestList(0));

      for (let seq = 0; seq < count; seq++) {
        const message = await executeMavLinkCommand(robot, missionCommand.requestItem(seq, 0));
        messages.push(message);
      }
      missionCommand.sendAck(robot, 0, 0);
    } catch (error) {
      missionCommand.sendAck(robot, 1, 0);
      return null;
    }

    switch (firmware) {
      case ArduPilot:
        return CommandConverter.ArduPilot.fromMavLink(messages);
      case PX4:
        return CommandConverter.PX4.fromMavLink(messages);
      default:
        return;
    }
  };

  const downloadFences = async () => {
    const messages = [];

    try {
      const { count } = await executeMavLinkCommand(robot, missionCommand.requestList(1));

      for (let seq = 0; seq < count; seq++) {
        const message = await executeMavLinkCommand(robot, missionCommand.requestItem(seq, 1));
        messages[seq] = message;
      }
      missionCommand.sendAck(robot, 0, 1);
    } catch (error) {
      missionCommand.sendAck(robot, 1, 1);
      return null;
    }

    return FenceConverter.fromMavLink(messages);
  };

  const drawCommand = () => {
    if (!commands) return;

    // 마커 정의
    const markers = [];
    commands.forEach((command, index) => {
      switch (command.type) {
        case 'doSetRoiLocation':
          markers.push({
            position: command.data.position,
            label: index + 1,
            isRoi: true,
          });
          break;

        case 'navLand':
        case 'navLoiterTime':
        case 'navLoiterToAlt':
        case 'navLoiterTurns':
        case 'navTakeoff':
        case 'navWaypoint':
          markers.push({
            position: command.data.position,
            label: index + 1,
          });
          break;

        case 'navReturnToLaunch':
          if (!homePosition) break;
          markers.push({
            position: homePosition,
            label: index + 1,
          });
          break;

        default:
          break;
      }
    });
    const { paths } = OlMap.addWays(robot.id, markers, robot.color);

    // Mouse over 시 강조
    const handleHover = new Select({
      condition: pointerMove,
      layers: [paths],
    });
    handleHover.on('select', (e) => {
      if (e.selected.length) {
        const { flatCoordinates } = e.selected[0].values_.geometry;
        const distanceValue =
          1000 *
          distance(
            toLonLat([flatCoordinates[0], flatCoordinates[1]]),
            toLonLat([flatCoordinates[2], flatCoordinates[3]])
          );

        dispatch(actions.callout.setText(`${distanceValue.toFixed(1)}m`));
      } else {
        dispatch(actions.callout.setText(null));
      }
    });
    map.addInteraction(handleHover);
  };

  const drawFence = () => {
    if (!fences) return;

    OlMap.addFences(robot.id, fences, robot.color);
  };
};

export default useMission;
